<!-- Table of Contents -->
<div class="row mb-4">
    <div class="col-md-4">
        <a href="#section-templates" data-type="toc-link" class="text-decoration-none">
            <div class="card card-hover h-100">
                <div class="card-body text-center p-4">
                    <span class="d-block mb-3">
                        <iconify-icon icon="solar:document-text-line-duotone" class="fs-8 text-primary"></iconify-icon>
                    </span>
                    <h5 class="fw-semibold mb-1">Templates</h5>
                    <p class="text-muted mb-0 fs-3">Create templates, use placeholders, and generate documents</p>
                </div>
            </div>
        </a>
    </div>
    <div class="col-md-4">
        <a href="#section-packs" data-type="toc-link" class="text-decoration-none">
            <div class="card card-hover h-100">
                <div class="card-body text-center p-4">
                    <span class="d-block mb-3">
                        <iconify-icon icon="solar:box-line-duotone" class="fs-8 text-primary"></iconify-icon>
                    </span>
                    <h5 class="fw-semibold mb-1">Packs</h5>
                    <p class="text-muted mb-0 fs-3">Bundle templates and generate merged documents</p>
                </div>
            </div>
        </a>
    </div>
    <div class="col-md-4">
        <a href="#section-api" data-type="toc-link" class="text-decoration-none">
            <div class="card card-hover h-100">
                <div class="card-body text-center p-4">
                    <span class="d-block mb-3">
                        <iconify-icon icon="solar:code-line-duotone" class="fs-8 text-primary"></iconify-icon>
                    </span>
                    <h5 class="fw-semibold mb-1">Developer API</h5>
                    <p class="text-muted mb-0 fs-3">Integrate document generation into your applications</p>
                </div>
            </div>
        </a>
    </div>
</div>

<!-- Section 1: Templates Guide -->
<div class="card mb-4" id="section-templates">
    <div class="card-header bg-transparent">
        <h4 class="mb-0">
            <iconify-icon icon="solar:document-text-line-duotone" class="text-primary me-2"></iconify-icon>
            Templates Guide
        </h4>
    </div>
    <div class="card-body p-0">
        <div class="accordion accordion-flush" id="accordionTemplates">

            <!-- Creating a Template -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseCreating">
                        Creating a Template
                    </button>
                </h2>
                <div id="collapseCreating" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Follow these steps to create a new template:</p>
                        <ol>
                            <li class="mb-2">
                                <strong>Name your template</strong> &mdash; Give it a clear, descriptive name so you can easily find it later.
                            </li>
                            <li class="mb-2">
                                <strong>Add a description</strong> &mdash; Optionally describe what this template is used for.
                            </li>
                            <li class="mb-2">
                                <strong>Upload your .docx file</strong> &mdash; Drag and drop or click to upload your Word document. Maximum file size is <strong>10 MB</strong>. Only <code>.docx</code> files are supported.
                            </li>
                            <li class="mb-2">
                                <strong>Save</strong> &mdash; Click save to create your template. The system will automatically detect all placeholders in your document.
                            </li>
                        </ol>
                        <div class="alert alert-info d-flex align-items-center mt-3" role="alert">
                            <iconify-icon icon="solar:info-circle-line-duotone" class="fs-5 me-2"></iconify-icon>
                            <div>Your .docx file should contain placeholder syntax (see below) wherever you want dynamic content to appear.</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Placeholder Syntax -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapsePlaceholders">
                        Placeholder Syntax
                    </button>
                </h2>
                <div id="collapsePlaceholders" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Placeholders are special tags in your .docx template that get replaced with actual values when generating a document.</p>

                        <!-- Simple Text -->
                        <h6 class="fw-semibold mt-4 mb-2">Simple Text</h6>
                        <p>Basic text replacement. Optionally provide a default value if the field is empty.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{customer_name}}</code><br>
                            <code>{{company | default:N/A}}</code>
                        </div>

                        <!-- Numbers -->
                        <h6 class="fw-semibold mt-4 mb-2">Numbers</h6>
                        <p>Format numbers as currency or with specific decimal places and locale.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{price | format:currency | decimals:2 | currency:EUR | locale:de-DE}}</code><br>
                            <code>{{quantity | format:number | decimals:0}}</code>
                        </div>

                        <!-- Dates -->
                        <h6 class="fw-semibold mt-4 mb-2">Dates</h6>
                        <p>Format date values using a date format pattern.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{invoice_date | format:DD.MM.YYYY}}</code><br>
                            <code>{{created_at | format:YYYY-MM-DD}}</code>
                        </div>

                        <!-- Images -->
                        <h6 class="fw-semibold mt-4 mb-2">Images</h6>
                        <p>Insert images into your document. Specify width and height in centimeters. When sending data via API, upload the image using the <code>image_&lt;key&gt;</code> field name.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{logo | width:6cm | height:4cm}}</code><br>
                            <code>{{signature | width:8cm | height:3cm}}</code>
                        </div>

                        <!-- Conditionals -->
                        <h6 class="fw-semibold mt-4 mb-2">Conditionals</h6>
                        <p>Show or hide content based on conditions. Supports comparison operators and logical operators.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{#if show_discount}}</code><br>
                            <code>&nbsp;&nbsp;Discount applied!</code><br>
                            <code>{{#else}}</code><br>
                            <code>&nbsp;&nbsp;No discount</code><br>
                            <code>{{/if}}</code>
                        </div>
                        <p class="mt-2">Available operators:</p>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th>Operator</th>
                                        <th>Description</th>
                                        <th>Example</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td><code>==</code></td><td>Equal to</td><td><code>{{#if status == "active"}}</code></td></tr>
                                    <tr><td><code>!=</code></td><td>Not equal to</td><td><code>{{#if type != "draft"}}</code></td></tr>
                                    <tr><td><code>&gt;</code></td><td>Greater than</td><td><code>{{#if amount > 100}}</code></td></tr>
                                    <tr><td><code>&lt;</code></td><td>Less than</td><td><code>{{#if quantity < 10}}</code></td></tr>
                                    <tr><td><code>&gt;=</code></td><td>Greater or equal</td><td><code>{{#if score >= 50}}</code></td></tr>
                                    <tr><td><code>&lt;=</code></td><td>Less or equal</td><td><code>{{#if age <= 18}}</code></td></tr>
                                    <tr><td><code>&amp;&amp;</code></td><td>Logical AND</td><td><code>{{#if active && verified}}</code></td></tr>
                                    <tr><td><code>||</code></td><td>Logical OR</td><td><code>{{#if admin || manager}}</code></td></tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Loops -->
                        <h6 class="fw-semibold mt-4 mb-2">Loops</h6>
                        <p>Repeat content for each item in a list. Useful for tables, line items, and lists.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{#each items}}</code><br>
                            <code>&nbsp;&nbsp;{{@number}}. {{name}} - {{price}}</code><br>
                            <code>{{/each}}</code>
                        </div>
                        <p class="mt-2">Available loop variables:</p>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered">
                                <thead class="table-light">
                                    <tr>
                                        <th>Variable</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td><code>@index</code></td><td>Zero-based index of current item</td></tr>
                                    <tr><td><code>@number</code></td><td>One-based number of current item</td></tr>
                                    <tr><td><code>@first</code></td><td>True if this is the first item</td></tr>
                                    <tr><td><code>@last</code></td><td>True if this is the last item</td></tr>
                                    <tr><td><code>@length</code></td><td>Total number of items in the list</td></tr>
                                    <tr><td><code>@item</code></td><td>The current item value (for simple arrays)</td></tr>
                                </tbody>
                            </table>
                        </div>

                        <!-- Text Transforms -->
                        <h6 class="fw-semibold mt-4 mb-2">Text Transforms</h6>
                        <p>Transform the case of text values.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>{{name | uppercase}}</code><br>
                            <code>{{email | lowercase}}</code><br>
                            <code>{{title | capitalize}}</code>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Using the Test Center -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseTestCenter">
                        Using the Test Center
                    </button>
                </h2>
                <div id="collapseTestCenter" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>The Test Center lets you preview your template with sample data before using it in production.</p>
                        <ol>
                            <li class="mb-2">
                                <strong>Open a template</strong> &mdash; Navigate to your template's detail page.
                            </li>
                            <li class="mb-2">
                                <strong>Switch to the Test Center tab</strong> &mdash; Click on the "Test Center" tab at the top.
                            </li>
                            <li class="mb-2">
                                <strong>Fill in test values</strong> &mdash; Enter sample data for each placeholder detected in your template.
                            </li>
                            <li class="mb-2">
                                <strong>Generate</strong> &mdash; Click the generate button to create a test document.
                            </li>
                            <li class="mb-2">
                                <strong>Download</strong> &mdash; Download the generated PDF to review the output.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

        </div>
    </div>
</div>

<!-- Section 2: Packs Guide -->
<div class="card mb-4" id="section-packs">
    <div class="card-header bg-transparent">
        <h4 class="mb-0">
            <iconify-icon icon="solar:box-line-duotone" class="text-primary me-2"></iconify-icon>
            Packs Guide
        </h4>
    </div>
    <div class="card-body p-0">
        <div class="accordion accordion-flush" id="accordionPacks">

            <!-- What are Packs -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseWhatArePacks">
                        What are Packs
                    </button>
                </h2>
                <div id="collapseWhatArePacks" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Packs let you bundle multiple templates together and process them in a single request. The result is a single merged PDF containing all generated documents in the order you define.</p>
                        <div class="alert alert-warning d-flex align-items-center mt-3" role="alert">
                            <iconify-icon icon="solar:crown-line-duotone" class="fs-5 me-2"></iconify-icon>
                            <div>Packs require a <strong>Business</strong> or <strong>Enterprise</strong> plan. <a href="/premium">Upgrade your plan</a> to use this feature.</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Creating a Pack -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseCreatingPack">
                        Creating a Pack
                    </button>
                </h2>
                <div id="collapseCreatingPack" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <ol>
                            <li class="mb-2">
                                <strong>Name your pack</strong> &mdash; Give it a clear name that describes the bundle.
                            </li>
                            <li class="mb-2">
                                <strong>Add a description</strong> &mdash; Optionally describe the purpose of this pack.
                            </li>
                            <li class="mb-2">
                                <strong>Select templates</strong> &mdash; Drag and drop templates from the available list to add them to the pack. You can reorder them by dragging.
                            </li>
                            <li class="mb-2">
                                <strong>Save</strong> &mdash; Click save to create your pack. The templates will be processed in the order shown.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

            <!-- Pack Test Generation -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapsePackTest">
                        Pack Test Generation
                    </button>
                </h2>
                <div id="collapsePackTest" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Test your pack by generating a merged document with sample data:</p>
                        <ol>
                            <li class="mb-2">
                                <strong>Open the pack</strong> &mdash; Navigate to the pack's detail page.
                            </li>
                            <li class="mb-2">
                                <strong>Fill in values per template</strong> &mdash; Each template in the pack has its own set of placeholders. Fill in test data for each one.
                            </li>
                            <li class="mb-2">
                                <strong>Generate</strong> &mdash; Click generate to create the merged PDF containing all templates.
                            </li>
                            <li class="mb-2">
                                <strong>Download</strong> &mdash; Download and review the generated PDF.
                            </li>
                        </ol>
                    </div>
                </div>
            </div>

        </div>
    </div>
</div>

<!-- Section 3: Developer API -->
<div class="card mb-4" id="section-api">
    <div class="card-header bg-transparent">
        <h4 class="mb-0">
            <iconify-icon icon="solar:code-line-duotone" class="text-primary me-2"></iconify-icon>
            Developer API
        </h4>
    </div>
    <div class="card-body p-0">
        <div class="accordion accordion-flush" id="accordionApi">

            <!-- API Documentation -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseApiDocs">
                        API Documentation
                    </button>
                </h2>
                <div id="collapseApiDocs" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Full API documentation with detailed endpoint descriptions, request/response examples, and error codes.</p>
                        <a href="#" class="btn btn-outline-primary">
                            <iconify-icon icon="solar:document-text-line-duotone" class="me-1"></iconify-icon>
                            View API Documentation
                        </a>
                    </div>
                </div>
            </div>

            <!-- Swagger API -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseSwagger">
                        Swagger API
                    </button>
                </h2>
                <div id="collapseSwagger" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Interactive Swagger UI for exploring and testing API endpoints directly in your browser.</p>
                        <a href="#" class="btn btn-outline-primary">
                            <iconify-icon icon="solar:code-line-duotone" class="me-1"></iconify-icon>
                            Open Swagger UI
                        </a>
                    </div>
                </div>
            </div>

            <!-- Authentication -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseAuth">
                        Authentication
                    </button>
                </h2>
                <div id="collapseAuth" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>All API requests must be authenticated. There are two authentication methods available:</p>

                        <h6 class="fw-semibold mt-4 mb-2">API Token Authentication</h6>
                        <p>Use an API token for server-to-server integrations. Pass the token in the <code>x-apitoken</code> header.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>x-apitoken: your-api-token-here</code>
                        </div>
                        <p>You can create and manage API tokens in the <a href="/api-tokens">API Tokens</a> settings page.</p>

                        <h6 class="fw-semibold mt-4 mb-2">Session Authentication</h6>
                        <p>Use a session token for browser-based or user-scoped requests. Pass the token in the <code>x-sessiontoken</code> header.</p>
                        <div class="bg-light rounded p-3 mb-3">
                            <code>x-sessiontoken: your-session-token-here</code>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Generation Endpoints -->
            <div class="accordion-item">
                <h2 class="accordion-header">
                    <button class="accordion-button collapsed" type="button" data-type="accordion-toggle" data-target="#collapseGeneration">
                        Generation Endpoints
                    </button>
                </h2>
                <div id="collapseGeneration" class="accordion-collapse collapse">
                    <div class="accordion-body">
                        <p>Generate documents from your templates via the API. Two output formats are available:</p>

                        <div class="table-responsive mb-4">
                            <table class="table table-sm table-bordered" style="color: white;">
                                <thead>
                                    <tr>
                                        <th style="color: white;">Endpoint</th>
                                        <th style="color: white;">Output</th>
                                        <th style="color: white;">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>POST /v1/generation/pdf</code></td>
                                        <td style="color: white;">PDF</td>
                                        <td style="color: white;">Generates a PDF document from a .docx template</td>
                                    </tr>
                                    <tr>
                                        <td><code>POST /v1/generation/html</code></td>
                                        <td style="color: white;">HTML</td>
                                        <td style="color: white;">Generates an HTML document from a .docx template (see limitations below)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h6 class="fw-semibold mt-4 mb-2">Request Format</h6>
                        <p>Both endpoints accept <code>multipart/form-data</code> with the following fields:</p>
                        <div class="table-responsive mb-3">
                            <table class="table table-sm table-bordered" style="color: white;">
                                <thead>
                                    <tr>
                                        <th style="color: white;">Field</th>
                                        <th style="color: white;">Type</th>
                                        <th style="color: white;">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>data</code></td>
                                        <td style="color: white;">JSON string</td>
                                        <td style="color: white;">Request payload (see below)</td>
                                    </tr>
                                    <tr>
                                        <td><code>image_&lt;key&gt;</code></td>
                                        <td style="color: white;">File (optional)</td>
                                        <td style="color: white;">Image files for image placeholders, e.g. <code>image_logo</code>, <code>image_signature</code>. Max 10 MB each.</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <p>The <code>data</code> field is a JSON string with the following properties:</p>
                        <div class="bg-light rounded p-3 mb-3">
<pre class="mb-0"><code>{
  "templateId": "your-template-id",
  "variables": {
    "customer_name": "John Doe",
    "invoice_date": "2026-01-15"
  },
  "metadataOnly": false
}</code></pre>
                        </div>
                        <div class="table-responsive mb-3">
                            <table class="table table-sm table-bordered" style="color: white;">
                                <thead>
                                    <tr>
                                        <th style="color: white;">Property</th>
                                        <th style="color: white;">Type</th>
                                        <th style="color: white;">Required</th>
                                        <th style="color: white;">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td><code>templateId</code></td>
                                        <td style="color: white;">string</td>
                                        <td style="color: white;">Yes</td>
                                        <td style="color: white;">The UUID of the template to generate from</td>
                                    </tr>
                                    <tr>
                                        <td><code>variables</code></td>
                                        <td style="color: white;">object</td>
                                        <td style="color: white;">No</td>
                                        <td style="color: white;">Key-value pairs for template placeholders</td>
                                    </tr>
                                    <tr>
                                        <td><code>metadataOnly</code></td>
                                        <td style="color: white;">boolean</td>
                                        <td style="color: white;">No</td>
                                        <td style="color: white;">If <code>true</code>, returns file metadata instead of the file itself. Default: <code>false</code></td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <h6 class="fw-semibold mt-4 mb-2">Response</h6>
                        <p>On success, the API responds with HTTP <code>201 Created</code>.</p>
                        <p>By default, the response body is the generated file (<code>application/pdf</code> or <code>text/html</code>). When <code>metadataOnly</code> is <code>true</code>, the response is JSON:</p>
                        <div class="bg-light rounded p-3 mb-3">
<pre class="mb-0"><code>{
  "filename": "template.pdf",
  "filesize": 145678,
  "createdAt": "2026-01-15T10:30:00Z"
}</code></pre>
                        </div>

                        <h6 class="fw-semibold mt-4 mb-2">Error Codes</h6>
                        <div class="table-responsive">
                            <table class="table table-sm table-bordered" style="color: white;">
                                <thead>
                                    <tr>
                                        <th style="color: white;">Status</th>
                                        <th style="color: white;">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td><code>400</code></td><td style="color: white;">Bad request &mdash; invalid data, missing templateId, or template not active</td></tr>
                                    <tr><td><code>402</code></td><td style="color: white;">Payment required &mdash; monthly generation quota exceeded</td></tr>
                                    <tr><td><code>403</code></td><td style="color: white;">Forbidden &mdash; template access denied or API token expired</td></tr>
                                    <tr><td><code>404</code></td><td style="color: white;">Not found &mdash; template does not exist</td></tr>
                                </tbody>
                            </table>
                        </div>

                        <h6 class="fw-semibold mt-4 mb-2">HTML Generation &mdash; Supported Elements</h6>
                        <p>The following Word elements are fully supported in HTML output:</p>
                        <ul>
                            <li>Paragraphs, headings, lists (ordered and unordered)</li>
                            <li>Bold, italic, underline, strikethrough</li>
                            <li>Tables (basic structure)</li>
                            <li>Hyperlinks</li>
                            <li>Images (embedded as base64-encoded inline images)</li>
                        </ul>

                        <h6 class="fw-semibold mt-4 mb-2">HTML Generation &mdash; Limitations</h6>
                        <div class="alert alert-warning d-flex align-items-start mt-3" role="alert">
                            <iconify-icon icon="solar:danger-triangle-line-duotone" class="fs-5 me-2 mt-1"></iconify-icon>
                            <div>
                                The following Word features are <strong>not fully supported</strong> in HTML output:
                                <ul class="mb-0 mt-2">
                                    <li>Complex page layouts (columns, page breaks, margins)</li>
                                    <li>Headers and footers (page-level concepts not native to HTML)</li>
                                    <li>Precise font sizes and colors (HTML output uses semantic markup)</li>
                                    <li>Complex table formatting (e.g. merged cells)</li>
                                </ul>
                            </div>
                        </div>
                        <p>For pixel-perfect output that preserves the full Word layout, use the <code>POST /v1/generation/pdf</code> endpoint instead.</p>
                    </div>
                </div>
            </div>

        </div>
    </div>
</div>
